/**
 * LegalSection.tsx - Legal Information and Terms Configuration Section
 *
 * This component handles legal information configuration including organization
 * details, terms and conditions, and compliance requirements.
 *
 * Key Features:
 * - Organization legal information form
 * - Terms and conditions configuration
 * - Privacy policy and legal compliance
 * - Address and contact information
 * - Terms display options (URL, text, or none)
 * - Validation for required legal fields
 *
 * Legal Information Fields:
 * - Legal Name: Official organization name
 * - Address: Complete mailing address
 * - Contact Information: Phone and email
 * - Tax ID: Organization tax identification
 *
 * Terms Configuration:
 * - Terms Type: URL link, custom text, or none
 * - Terms URL: Link to external terms page
 * - Terms Text: Custom terms and conditions text
 * - Required Checkbox: Whether terms acceptance is required
 *
 * Compliance Features:
 * - Required field validation for legal compliance
 * - Address format validation
 * - URL validation for terms links
 * - Text length validation for custom terms
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, form validation, address formatting
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { LegalTermsAndConditionType } from "../../../enums";
import { CreateFundraiserModel } from "../../../models";
import PreviewIcon from "../../../../../../images/fundraiser/preview.svg";
import {
  CountryDropdown,
  CountryFlag,
  CountryService,
  CountryWithStateModel,
  StateModel,
} from "@mindmemobile/mm-payment-modules";
import { SingleSelect } from "@mindmemobile/mm-shared-global";
import { fundraiserStore } from "../../../stores";
import { StateCountryRow } from "../../donation-capture/DonationCaptureDonorStep";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns || 1}, 1fr)`};
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.donation-country-dropdown {
    .country-phone-wrapper {
      height: 100%;
      background: inherit;
    }
    button {
      background-color: rgb(255, 255, 255);
    }
    .country-dropdown-expanded {
      height: 100%;
      > div {
        height: 58px;
        border: 2px solid #e2e8f0;
        border-radius: 12px;
        color: #1e293b;
        background-color: rgb(255, 255, 255);
        padding-left: 12px;
        .custom-label {
          color: #1e293b;
        }
      }
    }
  }
  &.edit-fundraiser-dpdn {
    .dropdown-menu {
      width: calc(100% + 30px) !important;
      transform: translate(-12px, 38px) !important;
    }
  }
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 50px;
  margin-bottom: 16px;
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 24px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 50%;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  cursor: pointer;
`;

const CountrySelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// const CountryFlag = styled.div`
//   font-size: 20px;
// `;

const URLInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 50%;
`;

const URLInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const URLInput = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 16px;
  padding-right: 50px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PreviewButton = styled.button`
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 24px;
    height: 24px;
  }
`;
const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 16px;
  color: #374151;
  font-weight: 400;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

interface LegalSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  handleFieldBlur: (fieldName: string) => void;
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

const isValidUrl = (url: string): boolean => {
  if (!url) return true; // Empty string is considered valid

  const originalUrl = url.trim();
  if (originalUrl === "") return true; // Trimmed empty string is also valid

  let processedUrl = originalUrl;
  let startsWithWww = false;

  // Determine if the original URL started with 'www.'
  if (originalUrl.startsWith("www.")) {
    startsWithWww = true;
    // Prepend 'https://' for URL constructor if no scheme is present
    if (
      !originalUrl.startsWith("http://") &&
      !originalUrl.startsWith("https://")
    ) {
      processedUrl = `https://${originalUrl}`;
    }
  } else if (
    !originalUrl.startsWith("http://") &&
    !originalUrl.startsWith("https://")
  ) {
    // Prepend 'https://' for plain domains if no scheme is present
    processedUrl = `https://${originalUrl}`;
  }

  try {
    const urlObj = new URL(processedUrl);
    const hostname = urlObj.hostname;

    // 1. Check for valid protocols
    if (urlObj.protocol !== "http:" && urlObj.protocol !== "https:") {
      return false;
    }

    // 2. Allow localhost and IP addresses
    if (
      hostname === "localhost" ||
      /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
    ) {
      return true;
    }

    // 3. Stricter hostname validation for public internet domains
    // This regex ensures valid characters and a TLD of at least 2 characters.
    // It implicitly requires at least one dot for a valid TLD.
    const domainRegex =
      /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    if (!domainRegex.test(hostname)) {
      return false;
    }

    // 4. Conditional dot count validation based on 'www.' prefix
    const dotCount = hostname.split(".").length - 1;
    if (startsWithWww) {
      // If original URL started with 'www.', it must have at least 2 dots (e.g., www.example.com)
      return dotCount >= 2;
    } else {
      // If original URL did NOT start with 'www.', it must have at least 1 dot (e.g., example.com)
      return dotCount >= 1;
    }
  } catch (e) {
    // URL constructor throws an error for malformed URLs
    return false;
  }
};

export const LegalSection: React.FC<LegalSectionProps> = observer(
  ({ builderData, updateBuilderField, handleFieldBlur, onKeyDown }) => {
    const termsType = builderData.termsType || LegalTermsAndConditionType.None;
    const countries = fundraiserStore.countryStore.countriesWithState;
    const [states, setStates] = useState<StateModel[]>([]);

    useEffect(() => {
      if (countries) {
        const usCountry = fundraiserStore.countryStore.getUSCountry();
        if (builderData.countryId) {
          const country = countries.find((c) => c.id === builderData.countryId);
          updateBuilderField("country", country);
        } else if (usCountry) {
          updateBuilderField("country", usCountry);
          updateBuilderField("countryId", usCountry.id);
        }
      }
    }, []);

    useEffect(() => {
      if (builderData.country.id) {
        const countryObj = countries.find(
          (c) => c.abbreviationName === builderData.country.abbreviationName
        );
        setStates(countryObj?.states || []);
      } else {
        setStates([]);
      }
    }, [builderData.country, countries]);

    const countryStates = (
      countries.find((c) => c.id === builderData.country.id)?.states || []
    )
      .map((state) => {
        return {
          label: state.name,
          value: state.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return (
      <Section>
        <FormGrid columns={2}>
          <FormGroup>
            <Label htmlFor="legalName">Legal Name</Label>
            <Input
              id="legalName"
              type="text"
              placeholder="Business, organization, non-profit name..."
              value={builderData.legalName || ""}
              onChange={(e) => updateBuilderField("legalName", e.target.value)}
              onBlur={() => handleFieldBlur("legalName")}
            />
          </FormGroup>
          <div></div>
        </FormGrid>

        <FormGrid columns={2}>
          <FormGroup>
            <Label htmlFor="streetAddress1">Street Address 1</Label>
            <Input
              id="streetAddress1"
              type="text"
              placeholder="Enter street address"
              value={builderData.streetAddress1 || ""}
              onChange={(e) =>
                updateBuilderField("streetAddress1", e.target.value)
              }
              onBlur={() => handleFieldBlur("streetAddress1")}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="streetAddress2">Street Address 2</Label>
            <Input
              id="streetAddress2"
              type="text"
              placeholder="Apartment, suite, etc. (optional)"
              value={builderData.streetAddress2 || ""}
              onChange={(e) =>
                updateBuilderField("streetAddress2", e.target.value)
              }
              onBlur={() => handleFieldBlur("streetAddress2")}
            />
          </FormGroup>
        </FormGrid>

        <FormGrid
          columns={3}
          style={{ gridTemplateColumns: "388px 300px 145px" }}
        >
          <FormGroup>
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              type="text"
              placeholder="Enter city"
              value={builderData.city || ""}
              onChange={(e) => updateBuilderField("city", e.target.value)}
              onBlur={() => handleFieldBlur("city")}
            />
          </FormGroup>
          <StateCountryRow>
            <FormGroup>
              <Label htmlFor="state">State</Label>
              <SingleSelect
                selectedKey={builderData.state || ""}
                className="default-padding-search-dpdn single-select-donor-input"
                options={[countryStates || []]}
                nameKeys={["label"]}
                valueKeys={["value"]}
                placeholder="Select State"
                searchable={true}
                errorMessage="Select State"
                onChange={(option: any) => {
                  updateBuilderField("state", option.value);
                  updateBuilderField("stateId", option.value);
                }}
              />
            </FormGroup>
          </StateCountryRow>

          <FormGroup>
            <Label htmlFor="zip">Zip</Label>
            <Input
              id="zip"
              type="text"
              placeholder="Enter zip code"
              value={builderData.zip || ""}
              onChange={(e) => updateBuilderField("zip", e.target.value)}
              onBlur={() => handleFieldBlur("zip")}
            />
          </FormGroup>
        </FormGrid>

        <FormGrid columns={2}>
          <FormGroup>
            <Label htmlFor="country">Country</Label>
            <FormGroup className="donation-country-dropdown edit-fundraiser-dpdn">
              <CountryDropdown
                className="country-dropdown country-dropdown-expanded"
                selectedCountry={
                  countries.find((c) => c.id === builderData.countryId) ||
                  new CountryWithStateModel()
                }
                error={!builderData.country}
                errorMessage={"Please select country"}
                onCountryChange={(id: string) => {
                  updateBuilderField("countryId", id);
                  updateBuilderField(
                    "country",
                    countries.find((c) => c.id === id)
                  );
                  if (id !== builderData.countryId) {
                    updateBuilderField("state", "");
                    updateBuilderField("stateId", "");
                  }
                }}
                availableCountries={countries}
              />
            </FormGroup>
          </FormGroup>
          <div></div>
        </FormGrid>

        <FormGrid columns={1}>
          <FormGroup>
            <Label style={{ marginBottom: "6px" }}>Terms & Conditions</Label>
            <RadioRow>
              <RadioOptions>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="termsType"
                    value={LegalTermsAndConditionType.None}
                    checked={termsType === LegalTermsAndConditionType.None}
                    onChange={(e) =>
                      updateBuilderField("termsType", e.target.value)
                    }
                  />
                  None
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="termsType"
                    value={LegalTermsAndConditionType.ReferenceURL}
                    checked={
                      termsType === LegalTermsAndConditionType.ReferenceURL
                    }
                    onChange={(e) =>
                      updateBuilderField("termsType", e.target.value)
                    }
                  />
                  Reference a URL
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="termsType"
                    value={LegalTermsAndConditionType.CustomTerms}
                    checked={
                      termsType === LegalTermsAndConditionType.CustomTerms
                    }
                    onChange={(e) =>
                      updateBuilderField("termsType", e.target.value)
                    }
                  />
                  Custom terms
                </RadioOption>
              </RadioOptions>

              {(termsType === LegalTermsAndConditionType.ReferenceURL ||
                termsType === LegalTermsAndConditionType.CustomTerms) && (
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    checked={builderData.termsRequired || false}
                    onChange={(e) =>
                      updateBuilderField("termsRequired", e.target.checked)
                    }
                  />
                  <CheckboxLabel>Require donor acceptance</CheckboxLabel>
                </CheckboxContainer>
              )}
            </RadioRow>

            {termsType === LegalTermsAndConditionType.ReferenceURL && (
              <URLInputContainer style={{ width: "50%" }}>
                <URLInputWrapper>
                  <URLInput
                    type="url"
                    placeholder="Enter website URL with terms & conditions..."
                    hasError={builderData.termsUrlError || false}
                    value={builderData.termsUrl || ""}
                    onChange={(e) => {
                      updateBuilderField("termsUrl", e.target.value);
                      // Validate URL
                      const isValid = isValidUrl(e.target.value);
                      updateBuilderField(
                        "termsUrlError",
                        !isValid && e.target.value.length > 0
                      );
                    }}
                    onBlur={() => {
                      handleFieldBlur("termsUrl");
                      const isValid = isValidUrl(builderData.termsUrl || "");
                      updateBuilderField(
                        "termsUrlError",
                        !isValid && (builderData.termsUrl || "").length > 0
                      );
                    }}
                  />
                </URLInputWrapper>
                {builderData.termsUrl && (
                  <PreviewButton
                    type="button"
                    onClick={() =>
                      window.open(builderData.getTermsUrlWithHttp(), "_blank")
                    }
                    title="Preview URL"
                  >
                    <img src={PreviewIcon} alt="Preview" />
                  </PreviewButton>
                )}
              </URLInputContainer>
            )}

            {termsType === LegalTermsAndConditionType.CustomTerms && (
              <TextArea
                placeholder="Enter terms and conditions..."
                value={builderData.termsText || ""}
                onChange={(e) => {
                  updateBuilderField("termsText", e.target.value);
                  // Clear error state when user starts typing
                  if (builderData.termsTextError) {
                    updateBuilderField("termsTextError", false);
                  }
                }}
                onBlur={() => handleFieldBlur("termsText")}
                style={{
                  borderColor: builderData.termsTextError
                    ? "#dc2626"
                    : "#e2e8f0",
                  backgroundColor: builderData.termsTextError
                    ? "#fef2f2"
                    : "#ffffff",
                }}
                onKeyDown={(e) => {
                  if (onKeyDown) {
                    onKeyDown(e, true);
                  }
                }}
              />
            )}
          </FormGroup>
        </FormGrid>
      </Section>
    );
  }
);
